import { map } from "rxjs"
import { normalizeApiDate } from "../../shared/utils/api-date"
import { HttpClient, HttpParams } from "@angular/common/http"
import { inject, Injectable } from "@angular/core"
import { environment } from "../../../../environments/environment"
import {
  IMonitoredSpotPage,
  ISpot,
  ISpotDetail,
  ISpotRecommendation,
} from "../interfaces/spot-recommendation.interface"
import { NO_ERROR_HANDLING } from "../../shared/constants/strings"

export type RecommendationUseCase =
  | "video_streaming"
  | "web"
  | "email"
  | "social_media"
  | "gaming"
  | "voice_call"

export const INTERNET_ACTIVITIES: { id: RecommendationUseCase; label: string }[] = [
  { id: "web", label: "Web browsing" },
  { id: "video_streaming", label: "Video streaming" },
  { id: "email", label: "Email" },
  { id: "social_media", label: "Social media" },
  { id: "gaming", label: "Gaming" },
  { id: "voice_call", label: "Voice calls" },
]

export type RecommendationService =
  | "internet"
  | "study"
  | "cafe"
  | "classroom"
  | "lab"
  | "other"
export type RecommendationTimeSlot =
  | "now"
  | "morning"
  | "lunch"
  | "afternoon"
  | "evening"
  | "night"

@Injectable({ providedIn: "root" })
export class SpotRecommendationService {
  private readonly http = inject(HttpClient)

  getInternetRecommendations(useCase?: RecommendationUseCase) {
    return this.getRecommendations("internet", "now", useCase)
  }

  getRecommendations(
    service: RecommendationService,
    timeSlot: RecommendationTimeSlot,
    useCase?: RecommendationUseCase,
  ) {
    let params = new HttpParams()
      .set("service", service)
      .set("time_slot", timeSlot)
    if (useCase) {
      params = params.set("use_case", useCase)
    }

    return this.http.get<ISpotRecommendation[]>(
      `${environment.api.baseUrl}/RMBTControlServer/spots/recommendations`,
      {
        params,
        headers: { [NO_ERROR_HANDLING]: "true" },
      },
    )
  }

  getMonitoredSpots(search = "") {
    let params = new HttpParams().set("page", 1).set("size", 100)
    if (search.trim()) {
      params = params.set("search", search.trim())
    }
    return this.http.get<IMonitoredSpotPage>(
      `${environment.api.baseUrl}/RMBTControlServer/spots/monitored`,
      {
        params,
        headers: { [NO_ERROR_HANDLING]: "true" },
      },
    )
  }

  getSpots() {
    return this.http.get<ISpot[]>(
      `${environment.api.baseUrl}/RMBTControlServer/spots`,
      { headers: { [NO_ERROR_HANDLING]: "true" } },
    )
  }

  getSpotDetails(id: number, timeSlot: RecommendationTimeSlot) {
    const params = new HttpParams().set("time_slot", timeSlot)
    return this.http.get<ISpotDetail>(
      `${environment.api.baseUrl}/RMBTControlServer/spots/${id}`,
      { params, headers: { [NO_ERROR_HANDLING]: "true" } },
    ).pipe(map((spot) => ({
      ...spot,
      analysis: {
        ...spot.analysis,
        internet: spot.analysis.internet.map((item) => ({
          ...item, lastMonitoredAt: normalizeApiDate(item.lastMonitoredAt),
        })),
      },
    })))
  }
}
